import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Grid,
  GridItem,
  Text,
} from '@everlywell/ui-kit';
import { useMutation } from '@tanstack/react-query';
import useCart from 'components/Cart/Cart.hooks';
import React, { useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { removeLineItem, updateLineItemQuantity } from 'utils/api/cartApis';
import { ANALYTICS } from 'utils/constants/analytics';
import { useProductBySlug, useProductVariants } from 'utils/hooks';
import { LineItem, AxiosErrorResponse } from 'utils/types';

import CartEmptyState from '../CartEmptyState';
import CartFooter from '../CartFooter';
import CartLineItem from '../CartLineItem';
import CartPromoCode from '../CartPromoCode';
import CartSpinner from '../CartSpinner';
import CartSummary from '../CartSummary';
import CartSummaryCard from '../CartSummaryCard';
import CartUpsell from '../CartUpsell';
import upsellJson from '../CartUpsell/upsell-mapping.json';

export type CartDrawerProps = {};

const upsell: Record<string, any> = upsellJson;

/**
 * Cart Drawer component.
 */
function CartDrawer(props: CartDrawerProps): JSX.Element | null {
  const {
    closeCart,
    isCartOpen,
    order,
    refetchOrder,
    resetOrder,
    addItemToCart,
  } = useCart();
  const [isPromoCodeLoading, setIsPromoCodeLoading] = React.useState(false);
  const productVariants = useProductVariants();
  const productsBySlug = useProductBySlug(productVariants);
  const [isUpsellEnabled, setIsUpsellEnabled] = useState(true);

  const lineItems = React.useMemo(() => order?.line_items ?? [], [order]);

  useEffect(() => {
    if (isCartOpen) {
      analytics.track({
        event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
        data: {
          label: 'Cart V2',
        },
      });
    }
  }, [isCartOpen]);

  /**
   * Remove a line item from the cart.
   */
  const {
    mutate: _removeItemFromCart,
    isLoading: isRemovingItemFromCart,
    // TODO: handle error
    // error: removeItemFromCartError,
  } = useMutation(removeLineItem, {
    onSuccess: () => {
      refetchOrder?.();
    },
    onError: (error: AxiosErrorResponse) => {
      if (error.response?.status === 404) {
        resetOrder();
      }
    },
  });

  const removeProduct = (lineItemId: number) =>
    order &&
    lineItemId &&
    _removeItemFromCart({
      orderId: order?.id,
      lineItemId: lineItemId,
    });

  /**
   * Change the quantity of a line item in the cart.
   */
  const {
    mutate: _updateLineItemQuantity,
    isLoading: isUpdatingLineItemQuantity,
    // TODO: handle error
    // error: updateLineItemQuantityError,
  } = useMutation(updateLineItemQuantity, {
    onSuccess: () => {
      refetchOrder?.();
    },
    onError: (error: AxiosErrorResponse) => {
      if (error.response?.status === 404) {
        resetOrder();
      }
    },
  });

  const changeProductQuantity = (lineItem: LineItem) =>
    order &&
    lineItem.id &&
    _updateLineItemQuantity({
      orderId: order?.id,
      variantId: lineItem.variant_id,
      quantity: lineItem.quantity,
    });

  const showLoadingOverlay =
    isRemovingItemFromCart || isUpdatingLineItemQuantity;

  const isEmpty = !order || lineItems.length === 0;

  // ### Hardcoded upsell logic
  // Determine logic based off of the first line item in the cart
  const lineItemId = lineItems?.[0]?.product_id?.toString();
  const upsellItemId = lineItemId ? upsell[lineItemId]?.id : null;
  // const upsellItemTagline = lineItemId ? upsell[lineItemId]?.tagline : null;
  const upsellItemTagline = 'Customers who bought this item also bought';
  const upsellItem = productVariants[upsellItemId];
  let upsellProduct;
  if (lineItemId)
    upsellProduct =
      upsell[lineItemId] && productsBySlug[upsell[lineItemId]?.upsell_slug];

  // replace line item in cart with upsell item
  const replaceLineItem = (data: {
    newLineItemId: number;
    oldLineItemId: number;
  }) => {
    addItemToCart({
      variant_id: data.newLineItemId,
      quantity: 1,
    });
    removeProduct(data.oldLineItemId);
  };

  useEffect(() => {
    // Check if upsell item is in Cart, if so, disable upsell
    if (upsellItemId) {
      const cartUpsellItem = lineItems.find(
        (item) => item.product_id === Number(upsellItemId),
      );
      if (cartUpsellItem) {
        setIsUpsellEnabled(false);
      } else {
        setIsUpsellEnabled(true);
      }
    }
  }, [lineItems, upsellItemId]);

  const trackButtonClick = (buttonName: string, lineItem?: LineItem) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: `${buttonName} button`,
        product: lineItem?.product_name,
      },
    });
  };

  return (
    <Drawer
      isOpen={isCartOpen}
      placement={'right'}
      onClose={closeCart}
      blockScrollOnMount={false}
    >
      {/* NOTE: zIndex index is overrode in order to place Cart on top of the Chat Bubble */}
      <DrawerContent
        position="relative"
        gap={0}
        // height is set to 0 to prevent the modal container from taking up the full height of the screen
        // this way we make sure the overlay is not visible and user can interact with the page while the cart is open
        containerProps={{ zIndex: 9998, height: 0 }}
      >
        <DrawerHeader paddingX={[4, 8]} paddingBottom={4}>
          <Text
            as="h1"
            size="xl"
            color="tints.black"
            fontFamily="body"
            fontWeight="medium"
          >
            My Cart
          </Text>
          <DrawerCloseButton />
        </DrawerHeader>

        <DrawerBody paddingY={4} paddingX={[4, 8]} overflow="auto">
          <Grid spacing={3} alignEdges>
            {lineItems.length > 0 ? (
              <>
                {lineItems.map((item, index: number) => (
                  <GridItem key={`item-${index}`} width="100%">
                    <CartLineItem
                      key={item.id}
                      lineItem={item}
                      onDecrement={() => {
                        changeProductQuantity({
                          ...item,
                          quantity: item.quantity - 1,
                        });
                      }}
                      onIncrement={() => {
                        changeProductQuantity({
                          ...item,
                          quantity: item.quantity + 1,
                        });
                      }}
                      onRemove={() => {
                        if (item.id) {
                          removeProduct(item.id);
                          trackButtonClick('Remove', item);
                        }
                      }}
                    />
                  </GridItem>
                ))}

                {upsellItem && (
                  <GridItem width="100%">
                    <Text
                      as="h2"
                      size="lg"
                      color="tints.black"
                      fontFamily="body"
                      fontWeight="medium"
                      marginTop={4}
                    >
                      {upsellItemTagline}
                    </Text>
                    <Box>
                      <CartUpsell
                        productVariant={upsellItem}
                        onAddToCart={() => {
                          if (
                            upsell[lineItemId ?? '']?.replace_item &&
                            lineItems[0]?.id
                          ) {
                            replaceLineItem({
                              newLineItemId: upsellItem.variantId,
                              oldLineItemId: lineItems[0].id,
                            });
                            setIsUpsellEnabled(false);
                          } else {
                            addItemToCart({
                              variant_id: upsellItem.variantId,
                              quantity: 1,
                            });
                          }
                        }}
                        isEnabled={isUpsellEnabled}
                        productImage={upsellProduct?.boxImage?.gatsbyImageData}
                      />
                    </Box>
                  </GridItem>
                )}
                {!isEmpty ? (
                  <GridItem width="100%" display={['block', 'none']}>
                    <CartSummaryCard order={order} />
                  </GridItem>
                ) : null}
              </>
            ) : (
              <GridItem width="100%">
                <CartEmptyState />
              </GridItem>
            )}
          </Grid>
        </DrawerBody>
        {!isEmpty ? (
          <DrawerFooter
            background="tints.lightCream"
            paddingX={[4, 8]}
            paddingTop={6}
            paddingBottom={0}
            display={['none', 'flex']}
            flexDirection="column"
            alignItems="stretch"
            position="relative"
            gap={4}
          >
            <CartPromoCode onLoadingChange={setIsPromoCodeLoading} />
            <CartSummary.Details order={order} />
            <Box as="hr" borderColor="tints.darkCream" />
            <CartSummary.Total order={order} />
            {isPromoCodeLoading && <CartSpinner />}
          </DrawerFooter>
        ) : null}

        {/* This is shown to block user actions while the cart order is being updated */}
        {showLoadingOverlay && <CartSpinner />}
        {!isEmpty && <CartFooter />}
      </DrawerContent>
    </Drawer>
  );
}

export default CartDrawer;
